import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithStorage } from 'jotai/utils';
import {
  GeologicIntervalResponse,
  GeologicInterval,
} from 'types/api-responses';
import { formatFixed } from 'utils/number';
import type { PreviewData } from './add-structure-grid';

export function getBasinIntervals(
  data: GeologicIntervalResponse,
  basinNames: string[]
): Record<string, GeologicInterval[]> {
  // Validate inputs
  if (
    !data ||
    !data.geologicIntervals ||
    !Array.isArray(data.geologicIntervals)
  ) {
    return {};
  }

  if (!basinNames || !Array.isArray(basinNames) || basinNames.length === 0) {
    return {};
  }

  // Initialize result object with empty arrays for each basin name
  const result: Record<string, GeologicInterval[]> = {};
  basinNames.forEach((basinName) => {
    // Skip null or empty basin names
    if (basinName && typeof basinName === 'string' && basinName.trim() !== '') {
      result[basinName] = [];
    }
  });

  // Filter intervals by basin name and populate result
  data.geologicIntervals.forEach((interval) => {
    // Skip invalid intervals
    if (!interval || typeof interval !== 'object') return;

    const basinName = interval.basinName;

    // Check if this basin is in our target list and is valid
    if (
      basinName &&
      typeof basinName === 'string' &&
      basinName.trim() !== '' &&
      basinNames.includes(basinName)
    ) {
      // Add this interval to the corresponding basin's array
      result[basinName].push({
        id: interval.id,
        basinName: interval.basinName,
        geologicInterval: interval.geologicInterval,
        positionalIndex: interval.positionalIndex,
        age: interval.age,
        createdAt: interval.createdAt,
      });
    }
  });

  // Sort intervals by positionalIndex for each basin
  Object.keys(result).forEach((basinName) => {
    result[basinName].sort((a, b) => {
      // Handle null positionalIndex values
      if (a.positionalIndex === null) return 1;
      if (b.positionalIndex === null) return -1;
      return a.positionalIndex - b.positionalIndex;
    });
  });

  // Check if any requested basin name wasn't found in the data
  basinNames.forEach((basinName) => {
    if (
      basinName &&
      typeof basinName === 'string' &&
      basinName.trim() !== '' &&
      !result[basinName]
    ) {
      result[basinName] = []; // Set empty array for unfound basin names
    }
  });

  return result;
}

/**
 * The warning to show when the chosen projection puts the uploaded file entirely outside
 * the continental US, or null when it does not. Ingest rejects such a file, so the upload
 * form warns before the user submits it.
 *
 * @param {?PreviewData} preview The file's bounding box read in the chosen projection.
 * @return {?string} The warning.
 */
export const getOutsideUsWarning = (
  preview: PreviewData | null
): string | null => {
  if (preview?.overlapsConus !== false) return null;
  if (!preview.boundingBox || 'error' in preview.boundingBox) return null;

  const box = preview.boundingBox;
  const [minLon, maxLon, minLat, maxLat] = [
    box.min_x,
    box.max_x,
    box.min_y,
    box.max_y,
  ].map((value) => formatFixed(value, 2));

  return (
    `The structure grid (latitude ${minLat} to ${maxLat}, longitude ${minLon} to ${maxLon}) ` +
    'falls entirely outside the United States. ' +
    'Check that the selected projection matches your uploaded file.'
  );
};

export const structureGridMapConfigAtom = atomWithStorage(
  'structure-grid-map-config',
  getMapDefaultConfig({
    wells: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
      },
    },
    wellsV2: {
      checked: false,
      options: {
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
        showWellNames: false,
      },
    },
    developmentAreas: {
      checked: false,
      options: {
        units: false,
        horizontalUnits: false,
        verticalUnits: false,
        fieldUnits: false,
        pricing: false,
      },
    },
    basinTier: {
      checked: true,
      options: {
        basin: true,
        tier: false,
      },
    },
    typeCurves: { checked: false },
    scrollZoom: { checked: true },
    popups: { checked: true },
    structureGrids: {
      checked: false,
      selectedBasins: [],
      selections: {},
      options: {},
    },
    titleEntityAssets: {
      checked: false,
      options: {
        surfaceInterest: false,
        workingInterest: false,
        unleased: false,
        royaltyInterest: false,
      },
    },
  })
);
